import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertTriangle, RefreshCw, User, Clock, ChevronLeft, ChevronRight } from 'lucide-react';

export default function EmergencyHistoryPage() {
    const { api, user } = useAuth();
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ total: 0, limit: 20, skip: 0 });

    const fetchAlerts = async (skip = 0) => {
        setLoading(true);
        setError(null);
        try {
            const response = await api().get(`/emergency/history?limit=20&skip=${skip}`);
            setAlerts(response.data.alerts || []);
            setPagination({
                total: response.data.total || 0,
                limit: response.data.limit || 20,
                skip: skip
            });
        } catch (err) {
            console.error('Error fetching emergency history:', err);
            setError(err.response?.data?.detail || 'Erreur lors du chargement de l\'historique');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAlerts(0);
    }, []);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handlePrevPage = () => {
        const newSkip = Math.max(0, pagination.skip - pagination.limit);
        fetchAlerts(newSkip);
    };

    const handleNextPage = () => {
        const newSkip = pagination.skip + pagination.limit;
        if (newSkip < pagination.total) {
            fetchAlerts(newSkip);
        }
    };

    const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
    const totalPages = Math.ceil(pagination.total / pagination.limit);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500/10 rounded-lg">
                        <AlertTriangle className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-white">Historique des Urgences</h1>
                        <p className="text-sm text-gray-400">
                            {pagination.total} alerte{pagination.total !== 1 ? 's' : ''} au total
                        </p>
                    </div>
                </div>
                <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => fetchAlerts(pagination.skip)}
                    disabled={loading}
                >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Actualiser
                </Button>
            </div>

            {/* Error Message */}
            {error && (
                <Card className="bg-red-500/10 border-red-500/30">
                    <CardContent className="p-4">
                        <p className="text-red-400">{error}</p>
                    </CardContent>
                </Card>
            )}

            {/* Alerts List */}
            <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader className="border-b border-gray-700">
                    <CardTitle className="text-white text-lg">Alertes d'urgence</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-8 text-center">
                            <RefreshCw className="w-8 h-8 text-gray-500 animate-spin mx-auto mb-2" />
                            <p className="text-gray-400">Chargement...</p>
                        </div>
                    ) : alerts.length === 0 ? (
                        <div className="p-8 text-center">
                            <AlertTriangle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                            <p className="text-gray-400">Aucune alerte d'urgence enregistrée</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-700">
                            {alerts.map((alert) => (
                                <div key={alert.id} className="p-4 hover:bg-gray-700/30 transition-colors">
                                    <div className="flex items-start gap-4">
                                        <div className="p-2 bg-red-500/20 rounded-full flex-shrink-0">
                                            <AlertTriangle className="w-5 h-5 text-red-400" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-red-400 font-semibold">URGENCE</span>
                                                <span className="text-gray-500">•</span>
                                                <span className="text-gray-400 text-sm flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {formatDate(alert.created_at)}
                                                </span>
                                            </div>
                                            <p className="text-white mb-2">{alert.message}</p>
                                            <div className="flex items-center gap-1 text-sm text-gray-500">
                                                <User className="w-3 h-3" />
                                                <span>Signalé par: </span>
                                                <span className="text-gray-300">{alert.sender_name}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between p-4 border-t border-gray-700">
                        <p className="text-sm text-gray-400">
                            Page {currentPage} sur {totalPages}
                        </p>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handlePrevPage}
                                disabled={pagination.skip === 0 || loading}
                            >
                                <ChevronLeft className="w-4 h-4 mr-1" />
                                Précédent
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleNextPage}
                                disabled={pagination.skip + pagination.limit >= pagination.total || loading}
                            >
                                Suivant
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}
